import { LANG_LABELS } from "@speisekarte/core";
import type { Lang } from "@speisekarte/core";
import { useKept } from "../kept.ts";

/**
 * A choice of languages (Martin, 01.11.2026, all six languages): up to three as buttons, one tap
 * each, as with English alone; more as a list, which stays one line also on a phone. The same at
 * every place – the card in steps 2 and 3, the catalogues, a dish's form, „Übersetzungen“.
 */
export function Choice<K extends string>({ label, options, value, onChange, testid, labelled = false }: { label: string; options: readonly { key: K; label: string }[]; value: K; onChange: (key: K) => void; testid: string; labelled?: boolean }) {
  if (options.length <= 3) {
    return (
      <div className="chips lang-choice" role="group" aria-label={label} data-testid={testid}>
        {labelled && <span className="muted small">{label}</span>}
        {options.map((o) => (
          <button key={o.key} type="button" className={o.key === value ? "active" : ""} aria-pressed={o.key === value} onClick={() => onChange(o.key)} data-testid={`${testid}-${o.key}`}>
            {o.label}
          </button>
        ))}
      </div>
    );
  }
  return (
    <label className="lang-choice lang-list">
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value as K)} data-testid={`${testid}-list`}>
        {options.map((o) => (
          <option key={o.key} value={o.key}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * The language one translates into in the catalogues (S36): with several switched on one at a time,
 * the same in the list, a dish's form and „Übersetzungen“, kept while one looks elsewhere.
 */
export function useWorkLang(langs: readonly Lang[]): [Lang | undefined, (l: Lang) => void] {
  const [chosen, setChosen] = useKept<string>("work:lang", "");
  return [langs.find((l) => l === chosen) ?? langs[0], setChosen];
}

/** „Übersetzen in: Englisch | Spanisch …“; nothing with one language. */
export function WorkLang({ langs, value, onChange }: { langs: readonly Lang[]; value: Lang; onChange: (l: Lang) => void }) {
  if (langs.length < 2) return null;
  return <Choice label="Übersetzen in" options={langs.map((l) => ({ key: l, label: LANG_LABELS[l] }))} value={value} onChange={onChange} testid="work-lang" labelled />;
}
